/**
 * Command Palette persistence + ranking helpers.
 *
 * Keeps localStorage I/O out of the React component so the palette JSX
 * stays focused on rendering. All functions are pure-ish (touch
 * localStorage but don't allocate React state).
 */

const RECENTS_KEY = "mmntm:cmdk:recents";
const FREQ_KEY    = "mmntm:cmdk:freq";
const RECENTS_MAX = 8;

// ---------- Storage primitives ----------

function safeParse(json, fallback) {
  try {
    const v = JSON.parse(json);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

export function loadRecents() {
  if (typeof window === "undefined") return [];
  return safeParse(localStorage.getItem(RECENTS_KEY), []);
}

export function loadFreq() {
  if (typeof window === "undefined") return {};
  return safeParse(localStorage.getItem(FREQ_KEY), {});
}

/**
 * Record a selection. Dedupes by id (newer entry wins, moved to top),
 * trims to RECENTS_MAX, and increments frequency counter.
 */
export function recordSelection(item) {
  if (!item || !item.id) return;
  const recents = loadRecents().filter((r) => r.id !== item.id);
  recents.unshift({ ...item, timestamp: Date.now() });
  localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, RECENTS_MAX)));

  const freq = loadFreq();
  freq[item.id] = (freq[item.id] || 0) + 1;
  localStorage.setItem(FREQ_KEY, JSON.stringify(freq));
}

export function clearRecents() {
  localStorage.removeItem(RECENTS_KEY);
}

// ---------- Ranking ----------

/**
 * Lightweight fuzzy scorer — no external lib.
 *  - Exact label match  → 1000
 *  - Substring match    → 600 + leftmost-position bonus
 *  - Subsequence match  → 100 + per-char bonus
 *  - No match           → 0
 *
 * Both inputs are lower-cased by the caller.
 */
function fuzzyScore(label, query) {
  if (!query) return 1; // any non-zero so cmdk keeps the item visible
  if (label === query) return 1000;
  const idx = label.indexOf(query);
  if (idx >= 0) return 600 + Math.max(0, 50 - idx);
  // Subsequence: each char in query must appear in label in order.
  let li = 0, qi = 0, matched = 0;
  while (li < label.length && qi < query.length) {
    if (label[li] === query[qi]) { matched++; qi++; }
    li++;
  }
  if (qi !== query.length) return 0;
  return 100 + matched * 5;
}

/**
 * Build the cmdk `filter` function. Closes over recents+freq once per
 * palette open — cheap, and shields us from re-reading localStorage on
 * every keystroke.
 *
 * Item value convention used by CommandPalette.js:
 *   "<id> <label>"   for actions / nav / settings (id is "cmd-action-foo" etc.)
 *   "task-<uuid> <title>"
 *   "project-<uuid> <name>"
 *
 * Type boost: for short queries (≤3 chars) prefer actions/nav over
 * tasks/projects so typing "n" surfaces "New task" instead of every task
 * starting with N.
 */
export function makeFilter({ recents = [], freq = {} } = {}) {
  const recentIds = new Set(recents.map((r) => r.id));
  const recentRank = new Map(recents.map((r, i) => [r.id, recents.length - i]));

  return (value, search) => {
    const q = (search || "").trim().toLowerCase();
    const v = (value || "").toLowerCase();

    // First token = id (no spaces), rest = label
    const firstSpace = v.indexOf(" ");
    const id = firstSpace > 0 ? v.slice(0, firstSpace) : v;
    const label = firstSpace > 0 ? v.slice(firstSpace + 1) : "";

    let score = fuzzyScore(label || v, q);
    if (score === 0) return 0;

    // Recency / frequency boost
    if (recentIds.has(id)) score += 30 + (recentRank.get(id) || 0) * 4;
    score += Math.min(20, (freq[id] || 0) * 3);

    // Type boost on short queries — actions/nav rank above tasks/projects
    const isAction = id.startsWith("new-") || id.startsWith("nav-") || id.startsWith("set-")
                     || id === "toggle-theme" || id === "switch-theme" || id === "open-notifications"
                     || id === "sign-out" || id === "start-focus" || id === "quick-capture";
    if (q.length <= 3 && isAction) score += 40;

    return score;
  };
}
